//create a react functional component in typescript
import {
  useCallback,
  useEffect,
  useState,
  useContext,
  useMemo,
  type FC,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  type ColumnDef,
  type SortingState,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import AppContext from "../utils/appContext";
import { confirmDialog, showErrorToast } from "./ui/feedback";
import { BlockingSpinner } from "./ui";
import ContactMap from "./contactmap";

interface ContactRecord {
  ID: number;
  firstname?: string;
  lastname?: string;
  email?: string;
  phone?: string;
  title?: string;
  company?: string;
  contact_list?: string;
  street?: string;
  number?: string;
  postcode?: string;
  city?: string;
  country?: string;
  description?: string;
}

interface contactListProps {
  refreshCounter: number;
  showMap: boolean;
}

const textValue = (value: unknown) => String(value ?? "");

const parseContactList = (contactList?: string) => {
  if (!contactList) {
    return [];
  }

  try {
    const parsedContactList = JSON.parse(contactList);
    return Array.isArray(parsedContactList) ? parsedContactList : [];
  } catch {
    return [];
  }
};

const ContactList: FC<contactListProps> = ({ refreshCounter, showMap }) => {
  const [contacts, setContacts] = useState<Array<ContactRecord>>([]);
  const [loading, setLoading] = useState(false);
  const [selectedContacts, setSelectedContacts] = useState<Array<number>>([]);
  const [allSelected, setAllSelected] = useState(false);
  const [unfilteredContacts, setUnfilteredContacts] = useState<
    Array<ContactRecord>
  >([]);
  const [search, setSearch] = useState("");
  const [searchMessage, setSearchMessage] = useState("");
  const [contactLists, setContactLists] = useState<Array<any>>([]);
  const [selectedContactList, setSelectedContactList] = useState("all");
  const [postcodeFrom, setPostcodeFrom] = useState("");
  const [postcodeUntil, setPostcodeUntil] = useState("");
  const [sorting, setSorting] = useState<SortingState>([
    { id: "lastname", desc: false },
  ]);
  const context = useContext(AppContext);
  const navigate = useNavigate();

  const canDeleteContacts =
    process.env.REACT_APP_CONTACTS_CAN_DELETE_USER === "true";
  const canEditContacts =
    process.env.REACT_APP_CONTACTS_CAN_EDIT_USER === "true";

  const getContacts = async () => {
    setLoading(true);

    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const storedJWT = localStorage.getItem("jwt");

    user.email &&
      (await fetch(process.env.REACT_APP_API_URL + "/get-contacts.php", {
        method: "POST",
        mode: "cors",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + storedJWT,
        },
        body: JSON.stringify({
          email: user.email,
        }),
      })
        .then((response) => response.json())
        .then((data) => {
          console.log(data);
          setContacts(data);
          setUnfilteredContacts(data);
          setLoading(false);
        })
        .catch((error) => {
          // Handle network or server errors
          console.error();
          setLoading(false);
        }));
  };
  const getContactLists = async () => {
    setLoading(true);

    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const storedJWT = localStorage.getItem("jwt");

    user.email &&
      (await fetch(process.env.REACT_APP_API_URL + "/get-contact-lists.php", {
        method: "POST",
        mode: "cors",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + storedJWT,
        },
        body: JSON.stringify({
          email: user.email,
        }),
      })
        .then((response) => response.json())
        .then((data) => {
          setContactLists(data);
        })
        .catch((error) => {
          // Handle network or server errors
          console.error();
        }));
  };
  const selectContact = useCallback(
    (id: number) => {
      if (selectedContacts.includes(id)) {
        let newItems = selectedContacts.filter((item) => item !== id);
        setSelectedContacts(newItems);
        setAllSelected(false);
      } else {
        let newItems = [...selectedContacts, id];
        setSelectedContacts(newItems);
        setAllSelected(newItems.length === contacts.length);
      }
    },
    [contacts.length, selectedContacts],
  );
  const selectAll = useCallback(() => {
    if (allSelected) {
      setSelectedContacts([]);
    } else {
      let allItems = contacts.map((contact) => contact.ID);
      setSelectedContacts(allItems);
    }
    setAllSelected(!allSelected);
  }, [allSelected, contacts]);

  const getContactListNames = useCallback(
    (contact: ContactRecord) =>
      parseContactList(contact.contact_list)
        .map((list: number | string) => {
          const listName = contactLists.find((l: any) => l.ID === Number(list));
          return listName?.name;
        })
        .filter(Boolean)
        .join(", "),
    [contactLists],
  );

  const columns = useMemo<ColumnDef<ContactRecord>[]>(
    () => [
      ...(canDeleteContacts
        ? [
            {
              id: "select",
              enableSorting: false,
              header: () => (
                <button
                  aria-label={allSelected ? "Deselect all" : "Select all"}
                  className={`${
                    allSelected
                      ? "bg-default border-default"
                      : "bg-white border-gray-300"
                  } grid p-0.5 content-center inline-block w-5 h-5 border rounded cursor-pointer`}
                  onClick={selectAll}
                >
                  <span>
                    {allSelected && (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={2}
                        stroke="white"
                        className="size-6"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="m4.5 12.75 6 6 9-13.5"
                        />
                      </svg>
                    )}
                  </span>
                </button>
              ),
              cell: ({ row }) => (
                <button
                  aria-label={
                    selectedContacts.includes(row.original.ID)
                      ? "Deselect contact"
                      : "Select contact"
                  }
                  className={`${
                    selectedContacts.includes(row.original.ID)
                      ? "bg-default border-default"
                      : "bg-white border-gray-300"
                  } grid p-0.5 content-center inline-block w-5 h-5 border rounded cursor-pointer`}
                  onClick={(event) => {
                    event.stopPropagation();
                    selectContact(row.original.ID);
                  }}
                >
                  <span>
                    {selectedContacts.includes(row.original.ID) && (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={2}
                        stroke="white"
                        className="size-6"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="m4.5 12.75 6 6 9-13.5"
                        />
                      </svg>
                    )}
                  </span>
                </button>
              ),
              meta: { className: "w-[75px] min-w-[75px]" },
            } as ColumnDef<ContactRecord>,
          ]
        : []),
      {
        accessorKey: "lastname",
        header: "Last name",
        cell: ({ getValue }) => textValue(getValue()),
        meta: { className: "w-[150px] min-w-[150px]" },
      },
      {
        accessorKey: "firstname",
        header: "First name",
        cell: ({ getValue }) => textValue(getValue()),
        meta: { className: "w-[150px] min-w-[150px]" },
      },
      {
        accessorKey: "email",
        header: "E-mail",
        cell: ({ getValue }) => textValue(getValue()),
        meta: { className: "w-[180px] min-w-[180px]" },
      },
      {
        accessorKey: "phone",
        header: "Phone",
        cell: ({ getValue }) => textValue(getValue()),
        meta: { className: "w-[180px] min-w-[180px]" },
      },
      {
        accessorKey: "title",
        header: "Title",
        cell: ({ getValue }) => textValue(getValue()),
        meta: { className: "w-[150px] min-w-[150px]" },
      },
      {
        accessorKey: "company",
        header: "Company",
        cell: ({ getValue }) => textValue(getValue()),
        meta: { className: "w-[200px] min-w-[200px]" },
      },
      {
        id: "contactLists",
        header: "Contact lists",
        accessorFn: getContactListNames,
        cell: ({ getValue }) => textValue(getValue()),
        meta: { className: "w-[250px] min-w-[250px]" },
      },
      {
        id: "street",
        header: "Street",
        accessorFn: (contact) =>
          `${textValue(contact.street)} ${textValue(contact.number)}`.trim(),
        cell: ({ getValue }) => textValue(getValue()),
        meta: { className: "w-[180px] min-w-[180px]" },
      },
      {
        accessorKey: "postcode",
        header: "Postcode",
        cell: ({ getValue }) => textValue(getValue()),
        meta: { className: "w-[120px] min-w-[120px]" },
      },
      {
        accessorKey: "city",
        header: "City",
        cell: ({ getValue }) => textValue(getValue()),
        meta: { className: "w-[180px] min-w-[180px]" },
      },
    ],
    [
      allSelected,
      canDeleteContacts,
      getContactListNames,
      selectAll,
      selectContact,
      selectedContacts,
    ],
  );

  const table = useReactTable({
    data: contacts,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const logout = () => {
    localStorage.clear();
    context?.updateLoginStatus(false);
    navigate("/login");
  };
  const deleteSelected = async () => {
    const storedJWT = localStorage.getItem("jwt");
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    if (selectedContacts.length > 0) {
      if (
        await confirmDialog({
          title: "Delete contacts?",
          message: "Are you sure you want to delete the selected contacts?",
          confirmLabel: "Delete",
          variant: "danger",
        })
      ) {
        setLoading(true);

        try {
          const promises = selectedContacts.map(async (selectedContact) => {
            const response = await fetch(
              process.env.REACT_APP_API_URL + "/delete-contact.php",
              {
                method: "POST",
                mode: "cors",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: "Bearer " + storedJWT,
                },
                body: JSON.stringify({
                  email: user.email,
                  id: selectedContact,
                }),
              },
            );

            const data = await response.json();

            if (data === "Error decoding token: Expired token") {
              logout();
            }

            if (data.error) {
              showErrorToast(data.error);
            } else {
              setSelectedContacts([]);
              setAllSelected(false);
            }
          });

          await Promise.all(promises);
          getContacts();
        } catch (error) {
          console.error("Error deleting users:", error);
        }
      }
    }
  };
  const searchContacts = (e: React.ChangeEvent<HTMLInputElement>) => {
    let search = e.target.value;
    setSearch(search);
    setSearchMessage("");

    if (search !== "") {
      let newContacts = unfilteredContacts.filter((contact) => {
        return (
          textValue(contact.firstname)
            .toLowerCase()
            .includes(search.toLowerCase()) ||
          textValue(contact.lastname)
            .toLowerCase()
            .includes(search.toLowerCase()) ||
          textValue(contact.email)
            .toLowerCase()
            .includes(search.toLowerCase()) ||
          textValue(contact.title)
            .toLowerCase()
            .includes(search.toLowerCase()) ||
          textValue(contact.company)
            .toLowerCase()
            .includes(search.toLowerCase()) ||
          textValue(contact.postcode)
            .toLowerCase()
            .includes(search.toLowerCase()) ||
          textValue(contact.city)
            .toLowerCase()
            .includes(search.toLowerCase()) ||
          textValue(contact.country)
            .toLowerCase()
            .includes(search.toLowerCase()) ||
          textValue(contact.description)
            .toLowerCase()
            .includes(search.toLowerCase())
        );
      });
      setContacts(newContacts);
      if (newContacts.length === 0) {
        setSearchMessage("No contacts found");
      }
    } else {
      setContacts(unfilteredContacts);
    }
  };
  const emptySearch = () => {
    setSearch("");
    setSearchMessage("");
    setContacts(unfilteredContacts);
  };
  const filterByContactList = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSearch("");
    setSearchMessage("");
    console.log(e.target.value);
    if (e.target.value === "all") {
      setContacts(unfilteredContacts);
    } else {
      let groupID = parseInt(e.target.value);
      let newContacts = unfilteredContacts.filter((contact) => {
        let contactLists = parseContactList(contact.contact_list).map((id) =>
          Number(id),
        );
        return contactLists.includes(groupID);
      });
      setContacts(newContacts);
      if (newContacts.length === 0) {
        setSearchMessage("No contacts found");
      }
    }
    setSelectedContactList(e.target.value);
  };
  useEffect(() => {
    getContacts();
    getContactLists();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshCounter]);

  /*
  const exportToCSV = () => {
    const headers = [
      'ID',
      'First Name',
      'Last Name',
      'Email',
      'Phone',
      'Company',
      'Title',
      'Street',
      'Postcode',
      'City',
      'Country',
    ];
    const rows = contacts.map((contact) => [
      contact.ID,
      contact.firstname,
      contact.lastname,
      contact.email,
      contact.phone,
      contact.company,
      contact.title,
      contact.street + ' ' + contact.number,
      contact.postcode,
      contact.city,
      contact.country,
    ]);
    const escapeCSV = (field: any) => {
      const str = String(field ?? '').replace(/"/g, '""'); // escape "
      return `"${str}"`; // wrap in quotes
    };

    const csvContent = [headers, ...rows]
      .map((row) => row.map(escapeCSV).join(','))
      .join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = 'contacts.csv';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
  };*/

  return (
    <div className="fade-in ">
      <div className="pr-2">
        <h1 className="flex flex-wrap min-h-[2rem] mb-2 gap-2  items-center text-xl md:text-2xl">
          Contacts
        </h1>
      </div>
      <div className="relative items-center justify-between pr-2 mb-3 md:flex md:mb-0 md:pr-7 md:mb-5">
        <div className="flex flex-wrap items-start gap-1 md:order-1">
          <button
            className="flex items-center gap-1 group"
            onClick={() => navigate("/")}
          >
            <span className="text-xs text-gray-400 transition-all md:text-sm group-hover:text-gray-300">
              Home
            </span>
          </button>

          <button className="flex items-center gap-1 ">
            <span className="text-xs text-gray-400 transition-all md:text-sm ">{`>`}</span>
            <span className="text-xs transition-all text-gray-400 md:text-sm max-w-[120px] truncate">
              Contacts
            </span>
          </button>
        </div>
      </div>
      {Array.isArray(unfilteredContacts) && unfilteredContacts.length > 0 && (
        <div className="flex flex-wrap gap-4 mt-5 mb-10">
          <div className="relative flex items-center content-center gap-1 text-xs transition-all border rounded-md text-default hover:bg-shade">
            <input
              className="py-2.5 px-3 rounded-md outline-none placeholder-gray-400  border-suzy-gray"
              type="text"
              value={search}
              placeholder="Search contacts"
              onChange={searchContacts}
            />
            <div className="absolute right-1 top-1/2 -translate-y-2/4">
              {search.length > 0 && (
                <svg
                  className="w-4 h-4 cursor-pointer"
                  onClick={emptySearch}
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6 18 18 6M6 6l12 12"
                  />
                </svg>
              )}
            </div>
            <div className="absolute text-xs text-red-700 left-1 -bottom-5">
              <p>{searchMessage}</p>
            </div>
          </div>
          {contactLists.length > 0 && (
            <div className="relative flex content-center text-xs transition-all bg-white text-default ">
              <select
                className="text-xs block relative  cursor-pointer text-gray-400 bg-none w-full appearance-none hover:bg-shade border py-2.5 pl-3 pr-7 outline-none rounded-md"
                name="contact_lists"
                id="contact_lists"
                value={selectedContactList}
                onChange={filterByContactList}
              >
                <option value="all">All lists</option>
                {contactLists.map((list) => (
                  <option key={list.ID} value={list.ID}>
                    {list.name}
                  </option>
                ))}
              </select>
              <svg
                className="absolute w-3 h-3 stroke-gray-400 top-3.5 right-2 pointer-events-none"
                viewBox="0 0 14 8"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M1.30859 1.38867L6.85208 6.93184L12.3953 1.38867"
                  strokeWidth="1.7"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                ></path>
              </svg>
            </div>
          )}
          {process.env.REACT_APP_MAP === "true" && (
            <div className="relative flex gap-2 items-center content-center gap-1 text-xs transition-all border rounded-md text-default hover:bg-shade">
              <input
                type="text"
                value={postcodeFrom}
                onChange={(e) => setPostcodeFrom(e.target.value)}
                placeholder="Postcode from"
                className="py-2.5 px-3 rounded-md outline-none placeholder-gray-400  border-suzy-gray"
              />
              <input
                type="text"
                value={postcodeUntil}
                onChange={(e) => setPostcodeUntil(e.target.value)}
                placeholder="Postcode until"
                className="py-2.5 px-3 rounded-md outline-none placeholder-gray-400  border-suzy-gray"
              />
            </div>
          )}
          {process.env.REACT_APP_CONTACTS_CAN_DELETE_USER === "true" &&
            selectedContacts.length > 0 && (
              <button
                className="flex content-center py-2.5 px-3 items-center gap-1 text-xs transition-all rounded-md text-default ring-1 ring-inset ring-suzy-gray hover:bg-shade control-button"
                onClick={deleteSelected}
              >
                <span className="text-gray-500">Delete selected</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  className="w-4 stroke-red-500"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6 18 18 6M6 6l12 12"
                  />
                </svg>
              </button>
            )}
        </div>
      )}
      {loading ? (
        <BlockingSpinner />
      ) : showMap ? (
        <ContactMap contactLists={contactLists} contacts={contacts} />
      ) : (
        <div className="max-h-[60vh] max-w-full relative overflow-auto border-b border-slate-100 pb-5">
          <table className="w-full min-w-[1740px] table-fixed border-collapse">
            <thead className="sticky top-0 z-10 bg-white">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id} className="border-b border-suzy-gray">
                  {headerGroup.headers.map((header) => {
                    const meta = header.column.columnDef.meta as
                      { className?: string } | undefined;
                    const sorted = header.column.getIsSorted();

                    return (
                      <th
                        key={header.id}
                        className={`${meta?.className ?? ""} border-r border-suzy-gray px-6 py-3 text-left align-middle last:border-r-0`}
                        scope="col"
                      >
                        {header.isPlaceholder ? null : !header.column.getCanSort() ? (
                          flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )
                        ) : (
                          <button
                            className="flex w-full cursor-pointer items-center justify-between gap-2 text-left text-xs font-medium text-gray-400 hover:text-default md:text-sm"
                            onClick={header.column.getToggleSortingHandler()}
                          >
                            <span className="truncate">
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext(),
                              )}
                            </span>
                            <span className="w-3 shrink-0 text-[10px]">
                              {sorted === "asc"
                                ? "▲"
                                : sorted === "desc"
                                  ? "▼"
                                  : ""}
                            </span>
                          </button>
                        )}
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  onClick={
                    canEditContacts
                      ? () => navigate(`/contacts/${row.original.ID}`)
                      : undefined
                  }
                  className={`border-t border-suzy-gray transition-colors hover:bg-shade/50 ${
                    canEditContacts ? "cursor-pointer" : ""
                  }`}
                >
                  {row.getVisibleCells().map((cell) => {
                    const meta = cell.column.columnDef.meta as
                      { className?: string } | undefined;
                    const value = flexRender(
                      cell.column.columnDef.cell,
                      cell.getContext(),
                    );
                    const title = typeof value === "string" ? value : undefined;

                    return (
                      <td
                        key={cell.id}
                        title={title}
                        className={`${meta?.className ?? ""} overflow-hidden px-6 py-3 align-middle text-sm`}
                      >
                        <div className="truncate">{value}</div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          {table.getRowModel().rows.length === 0 && (
            <p className="py-8 text-sm text-gray-400">No contacts found</p>
          )}
        </div>
      )}
    </div>
  );
};
export default ContactList;
